/**
 * DropPoolPreview — sleek "What You Can Pull" strip shown inside drops that
 * have multiple cards in their pool. Two variants:
 *
 *   • compact  — bigger grid (2 cols mobile, 3 sm, 4 md+) rendered inside
 *                each drop card on listing pages (PlayPage, HomePage, etc.).
 *                Tap a tile → expand to a full lightbox view.
 *   • full     — taller scroll strip rendered inside the drop detail
 *                modal / checkout sheet, with full names & rarity pills.
 *
 * Per the spec: thumbnail + name + rarity pill ONLY. No odds, no print
 * runs, no metal — keeps the reveal mysterious. LEGENDS gradient ring
 * on rarity pills (yellow → orange → red).
 *
 * 2026-05-17: bumped compact tile size + switched to grid wrap per user
 * directive — horizontal scroll buried cards on mobile.
 */
import React, { useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Layers, X } from "lucide-react";
import { getMediaUrl } from "../../utils";

// Rarity → swatch colour ramp for the pill border. Each tier gets its own
// distinct ring so the eye picks out value at a glance — no two tiers share
// a colour. Legendary still uses the LEGENDS gradient for chase appeal.
const rarityRingClass = (rarity) => {
  const r = (rarity || "").toLowerCase();
  if (["legendary", "mythic", "1of1", "1of1s", "ultra", "chase"].includes(r)) {
    // Yellow → orange → red brand gradient (chase)
    return "border-transparent bg-clip-padding [background:linear-gradient(#0f0f17,#0f0f17)_padding-box,linear-gradient(90deg,#FCD34D,#F97316,#EF4444)_border-box] border-2";
  }
  if (["epic"].includes(r)) {
    // Purple — epic
    return "border-2 border-purple-500 shadow-[0_0_10px_-2px_rgba(168,85,247,0.55)]";
  }
  if (["rare"].includes(r)) {
    // Blue — rare
    return "border-2 border-blue-500 shadow-[0_0_10px_-2px_rgba(59,130,246,0.55)]";
  }
  if (["uncommon"].includes(r)) {
    // Green — uncommon
    return "border-2 border-emerald-500 shadow-[0_0_10px_-2px_rgba(16,185,129,0.55)]";
  }
  // Common → crisp gray ring
  return "border-2 border-gray-500/80";
};

const rarityPillClass = (rarity) => {
  const r = (rarity || "").toLowerCase();
  if (["legendary", "mythic", "1of1", "1of1s", "ultra", "chase"].includes(r)) {
    return "brand-gradient-text font-black";
  }
  if (["epic"].includes(r)) return "text-purple-300 font-bold";
  if (["rare"].includes(r)) return "text-blue-300 font-bold";
  if (["uncommon"].includes(r)) return "text-emerald-300 font-bold";
  return "text-gray-400 font-semibold";
};

export default function DropPoolPreview({ cards = [], variant = "compact", title, isHobbyDrop = false }) {
  const scrollRef = useRef(null);
  // Lightbox state: store the INDEX into `visible` (not the card object) so
  // prev/next swipe + keyboard nav can step through the pool cleanly.
  // `null` = closed. Switched from object → index 2026-05-17 to support
  // Pinterest-style swipe-through inside the lightbox.
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Filter pool cards: only show ones with an image and a name. AR drops
  // also pass `pool_cards` here once the admin form is updated.
  const visible = (cards || []).filter((c) => c && (c.image_url || c.card_image_url) && c.name);

  const isLightboxOpen = lightboxIdx !== null && lightboxIdx >= 0 && lightboxIdx < visible.length;
  const lightboxCard = isLightboxOpen ? visible[lightboxIdx] : null;

  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const goPrev = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i - 1 + visible.length) % visible.length));
  }, [visible.length]);
  const goNext = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i + 1) % visible.length));
  }, [visible.length]);

  // Keyboard nav (Esc + arrow keys) — only active while lightbox is open
  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLightboxOpen, closeLightbox, goPrev, goNext]);

  // Touch swipe handlers — capture start X on touchstart, fire prev/next
  // on touchend if horizontal travel exceeds 50px. Vertical or short
  // swipes are ignored so tapping (to close on backdrop) still works.
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const endTouch = (e.changedTouches && e.changedTouches[0]) || null;
    if (!endTouch) { touchStartX.current = null; return; }
    const dx = endTouch.clientX - touchStartX.current;
    const dy = endTouch.clientY - (touchStartY.current ?? endTouch.clientY);
    touchStartX.current = null;
    touchStartY.current = null;
    // Only register horizontal swipes (dx dominates dy) over 50px
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();  // swipe left → next
      else goPrev();          // swipe right → prev
    }
  };

  if (visible.length < 2) return null;  // 1 card = not really a "pool"

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 260, behavior: "smooth" });
  };

  // Compact = grid wrap (bigger tiles); Full = horizontal scroll strip.
  const isFull = variant === "full";

  const renderTile = (card, i) => {
    const img = card.image_url || card.card_image_url;
    const imgSrc = img.startsWith("http") ? img : getMediaUrl(img);
    // Find this card's index in `visible` (already-filtered list) so the
    // lightbox can step through with prev/next. `visible.indexOf(card)` is
    // safe because `card` is the same reference passed via map iteration.
    const visibleIdx = visible.indexOf(card);
    return (
      <button
        type="button"
        key={card.id || `${card.name}-${i}`}
        onClick={(e) => { e.stopPropagation(); setLightboxIdx(visibleIdx); }}
        className={`group text-left ${isFull ? "flex-shrink-0 snap-start w-24 sm:w-28" : "w-full"} cursor-zoom-in`}
        data-testid={`pool-card-${i}`}
        aria-label={`Preview ${card.name}`}
      >
        <div
          className={`aspect-[3/4] rounded-lg overflow-hidden ${isHobbyDrop ? "border-2 border-white/15" : rarityRingClass(card.rarity)} bg-black/60 relative transition-transform duration-300 group-hover:scale-[1.03] group-active:scale-[0.98]`}
        >
          <img
            src={imgSrc}
            alt={card.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
        <div className="mt-1.5 px-0.5">
          <p
            className={`text-white ${isFull ? "text-[11px]" : "text-[12px] sm:text-[13px]"} font-bold truncate leading-tight`}
            title={card.name}
          >
            {card.name}
          </p>
          {/* HOBBY drops: never expose generic rarity tiers
              (Common/Rare/Legendary). Prefer admin-set custom
              label, then series name, else show nothing.
              Standard EMERGE drops keep their rarity pill. */}
          {isHobbyDrop ? (
            (card.label || card.display_label) && (
              <p className={`uppercase tracking-wider ${isFull ? "text-[9px]" : "text-[10px]"} text-gray-400 font-semibold truncate`}>
                {card.label || card.display_label}
              </p>
            )
          ) : (
            card.rarity && (
              <p className={`uppercase tracking-wider ${isFull ? "text-[9px]" : "text-[10px]"} ${rarityPillClass(card.rarity)} truncate`}>
                {card.rarity}
              </p>
            )
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="relative" data-testid={`drop-pool-preview-${variant}`}>
      {/* Title row — shown for both variants so users always know what this is */}
      <div className="flex items-center justify-between mb-2">
        <div className={`flex items-center gap-1.5 text-gray-400 uppercase tracking-[0.18em] font-bold ${isFull ? "text-[10px]" : "text-[10px] sm:text-[11px]"}`}>
          <Layers size={isFull ? 12 : 12} className="text-amber-400" />
          <span>{title || "What You Can Pull"}</span>
          <span className="text-gray-600 normal-case tracking-normal font-normal">· {visible.length}</span>
        </div>
        {isFull && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => scroll(-1)}
              className="w-7 h-7 rounded-full bg-gray-800/80 hover:bg-gray-700 text-gray-300 flex items-center justify-center transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              className="w-7 h-7 rounded-full bg-gray-800/80 hover:bg-gray-700 text-gray-300 flex items-center justify-center transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {isFull ? (
        /* Full variant: horizontal scroll strip (drop detail / checkout). */
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-1 -mx-1 px-1"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {visible.map(renderTile)}
        </div>
      ) : (
        /* Compact variant: 2-up grid on narrow mobile (so tiles are large
           enough to read inside cramped parent cards), expanding to 3-4
           cols on wider viewports. Bumped from 3-cols-everywhere on
           2026-05-17 — tiles were illegible inside the home/play drop
           cards on mobile. Tap a tile to expand. */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-2.5">
          {visible.map(renderTile)}
        </div>
      )}

      {/* Lightbox — tap a tile to see the card art full-size. Closes on
          backdrop tap, X button, or Escape key.

          IMPORTANT: rendered via React Portal into document.body so the
          fixed-positioned backdrop ALWAYS covers the full viewport even
          when our parent (a drop card, carousel item, swipe container,
          etc.) has `overflow-hidden`, `transform`, or `filter` applied.
          Without the portal, a parent with `transform` re-roots the
          `fixed` element to that ancestor, causing the lightbox to be
          clipped INSIDE the originating drop card — making it look like
          the next drop's image is "leaking through". Reported & fixed
          2026-05-17. */}
      {lightboxCard && typeof document !== "undefined" && createPortal(
        (() => {
          // Compute the current image src from the lightbox card (no longer
          // baked onto the state object — derived fresh each render so swipe
          // nav always reads from the live `visible` array).
          const lbImg = lightboxCard.image_url || lightboxCard.card_image_url || "";
          const lbImgSrc = lbImg.startsWith("http") ? lbImg : getMediaUrl(lbImg);
          const hasMultiple = visible.length > 1;
          return (
            <div
              className="fixed inset-0 z-[2147483000] flex items-center justify-center bg-black/90 backdrop-blur-md p-6 animate-in fade-in duration-200"
              onClick={closeLightbox}
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              data-testid="pool-card-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label={`${lightboxCard.name} preview`}
            >
              {/* Close button — always rendered top-right */}
              <button
                type="button"
                onClick={closeLightbox}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                aria-label="Close preview"
                data-testid="pool-card-lightbox-close"
              >
                <X size={20} />
              </button>

              {/* Position counter — shows "3 / 12" at the top so users
                  immediately understand they can swipe to flip through the
                  rest of the pool. Hidden when only one card. */}
              {hasMultiple && (
                <div
                  className="absolute top-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/85 text-xs font-semibold tracking-wider tabular-nums"
                  data-testid="pool-card-lightbox-counter"
                >
                  {lightboxIdx + 1} / {visible.length}
                </div>
              )}

              {/* Prev / Next arrows — desktop affordance. Mobile users can
                  swipe horizontally (handled on the backdrop) but the
                  arrows stay visible at all sizes so the gesture is
                  discoverable. */}
              {hasMultiple && (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center transition-all backdrop-blur-sm"
                    aria-label="Previous card"
                    data-testid="pool-card-lightbox-prev"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center transition-all backdrop-blur-sm"
                    aria-label="Next card"
                    data-testid="pool-card-lightbox-next"
                  >
                    <ChevronRight size={22} />
                  </button>
                </>
              )}

              {/* Card stage — clicks bubble-stop so swipes on the card
                  itself still trigger the backdrop's touch handlers but
                  clicks don't close the lightbox. Keyed on lightboxIdx so
                  React fully replaces the <img>/<p> nodes on swipe (no
                  stale alt text or sub-pixel ghost frames). */}
              <div
                key={lightboxIdx}
                className="relative max-w-sm w-full animate-in fade-in slide-in-from-bottom-2 duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={`aspect-[3/4] rounded-2xl overflow-hidden ${isHobbyDrop ? "border-2 border-white/15" : rarityRingClass(lightboxCard.rarity)} bg-black/60 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.9)]`}
                >
                  <img
                    src={lbImgSrc}
                    alt={lightboxCard.name}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="text-white text-lg font-black uppercase tracking-wide" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                    {lightboxCard.name}
                  </p>
                  {isHobbyDrop ? (
                    (lightboxCard.label || lightboxCard.display_label) && (
                      <p className="uppercase tracking-[0.2em] text-xs text-gray-400 font-semibold mt-1">
                        {lightboxCard.label || lightboxCard.display_label}
                      </p>
                    )
                  ) : (
                    lightboxCard.rarity && (
                      <p className={`uppercase tracking-[0.2em] text-xs mt-1 ${rarityPillClass(lightboxCard.rarity)}`}>
                        {lightboxCard.rarity}
                      </p>
                    )
                  )}
                  {hasMultiple && (
                    <p className="text-white/40 text-[10px] tracking-[0.18em] uppercase mt-2.5 sm:hidden">
                      Swipe ← → to browse
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}
    </div>
  );
}
